import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { BACKEND_URL } from "../constants";
import { useAuth } from '../navigation/AuthProvider';

export const ImportExcelPopup = ({ isOpen, onClose, user, company, timeSchema, lastPublishedDate, selectedTimeSchema, onImportSuccess }) => {
  const { setLoading, loading, setProjects, projects } = useAuth();

  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMessages, setErrorMessages] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [popupLoading, setPopupLoading] = useState(false);

  const [parsedShifts, setParsedShifts] = useState([]);
  const [showPreviewPopup, setShowPreviewPopup] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const timeFormatRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;

  const updateShift = (date, shiftIndex, field, value) => {
    setParsedShifts(prev =>
      prev.map(day => {
        if (day.date !== date) return day;
        return {
          ...day,
          shifts: day.shifts.map((shift, idx) => {
            if (idx !== shiftIndex) return shift;
            return { ...shift, [field]: value };
          })
        };
      })
    );
  };

  const handleConfirmImport = async () => {
    // NEW: prevent double-click / multiple submits
    if (confirmLoading) return;

    try {
      setConfirmLoading(true);
      const finalShiftData = parsedShifts;

      console.groupCollapsed('%c[ImportExcel] POST -> backend payload', 'color:#0366d6;font-weight:bold');
      console.log(finalShiftData);
      console.groupEnd();

      const res = await axios.post(`${BACKEND_URL}/handle-import-excel-file-shifts-data`, {
        shifts: finalShiftData,
        userId: user._id,
        companyId: company._id,
        timeSchemaId: timeSchema,
        selectedMonth,
        selectedYear,
        lastPublishedDate
      }, { withCredentials: true });

      if (res.data.status === "success") {
        onImportSuccess(res.data.shifts);
        onClose();
        return window.location.reload();
      }
    } catch (err) {
      console.error("❌ Error during confirm import", err);
      alert("❌ Ett fel uppstod vid import.");
    } finally {
      setConfirmLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
    setErrorMessages([]);
  };

  // ===== Helpers: robust tidstolkning (men korrekt cell tack vare fast mappning) =====
  const fromExcelTimeNumber = (n) => {
    if (typeof n !== 'number' || !isFinite(n)) return null;
    const totalMinutes = Math.round((n % 1) * 24 * 60);
    const hh = Math.floor(totalMinutes / 60);
    const mm = totalMinutes % 60;
    const pad = (x) => String(x).padStart(2, '0');
    return `${pad(hh)}:${pad(mm)}`;
  };

  const normalizeTime = (input) => {
    if (input == null) return null;
    const s = String(input);
    const parts = s.split(":").map(p => p.replace(/\D/g, ""));
    let h = "00", m = "00";
    if (parts.length === 1) {
      const only = parts[0] || "";
      if (only.length <= 2) h = only.padStart(2, "0");
      else if (only.length === 3) { h = only[0]; m = only.slice(1); }
      else { h = only.slice(0,2); m = only.slice(2,4); }
    } else {
      h = (parts[0] || "0").padStart(2, "0");
      m = (parts[1] || "0").padEnd(2, "0").slice(0,2);
    }
    return `${h}:${m}`;
  };

  const parseAnyTimeRange = (raw) => {
    if (raw == null || raw === "") return { start: null, end: null };
    if (typeof raw === 'number') {
      return { start: fromExcelTimeNumber(raw), end: null };
    }
    const s = String(raw).trim();
    const parts = s.split(/[-–—]/).map(p => p.trim());
    const isNum = (x) => /^\d*\.?\d+$/.test(x.replace(",", "."));
    if (parts.length === 2) {
      const p0 = parts[0].replace(",", ".");
      const p1 = parts[1].replace(",", ".");
      const start = isNum(p0) ? fromExcelTimeNumber(parseFloat(p0)) : normalizeTime(parts[0]);
      const end   = isNum(p1) ? fromExcelTimeNumber(parseFloat(p1)) : normalizeTime(parts[1]);
      return { start, end };
    }
    const one = s.replace(",", ".");
    const t = isNum(one) ? fromExcelTimeNumber(parseFloat(one)) : normalizeTime(s);
    return { start: t, end: null };
  };
  // ================================================================================

  const handleFileUpload = async () => {
    if (!selectedFile || !selectedMonth || !selectedYear) {
      alert("❌ Du måste välja månad och år innan du kan importera.");
      return;
    }
    if (!selectedTimeSchema) {
      return alert("Schema måste vara valt för att importera skift!");
    }

    try {
      setTimeout(() => setPopupLoading(true), 0);
      const reader = new FileReader();
      reader.readAsArrayBuffer(selectedFile);

      reader.onload = async (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        let rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null });

        console.groupCollapsed("%c[ImportExcel] Grundinfo", "color:#0366d6;font-weight:bold");
        console.log("Vald månad/år:", selectedMonth, selectedYear);
        console.log("Antal rader:", rows.length);
        console.groupEnd();

        // ===== 1) Hitta datumceller: ENDAST heltal 1..31 =====
        let datePositions = [];
        for (let r = 0; r < rows.length; r++) {
          const row = rows[r] || [];
          for (let c = 0; c < row.length; c++) {
            const v = row[c];
            if (Number.isInteger(v) && v >= 1 && v <= 31) {
              // FAST MAPPNING: [dateCol-2, dateCol-1, dateCol]
              datePositions.push({
                date: v,
                row: r,
                col: c,
                processColumns: [c - 2, c - 1, c]  // <-- viktig skillnad: ingen heuristik
              });
            }
          }
        }

        console.groupCollapsed("%c[Scan] Hittade datum", "color:#2aa198;font-weight:bold");
        console.table(datePositions.slice(0, 100));
        console.groupEnd();

        if (!datePositions.length) {
          setPopupLoading(false);
          return alert("Hittade inga datum (1–31) i arket.");
        }

        // ===== 2) Sortera och inferera månad/år =====
        datePositions.sort((a, b) => a.row - b.row || a.col - b.col);
        let inferredMonth = parseInt(selectedMonth, 10);
        let inferredYear  = parseInt(selectedYear, 10);

        // ===== 2) Fördela datum över föregående, vald och nästa månad =====
        const importMonth = parseInt(selectedMonth, 10);
        const importYear  = parseInt(selectedYear, 10);

        let currentMonth = importMonth;
        let currentYear  = importYear;

        // 🔎 hitta första 1 → start på vald månad
        const firstDayIndex = datePositions.findIndex(p => p.date === 1);

        datePositions = datePositions.map((pos, i) => {
          const day = pos.date;

          // Före första 1 → föregående månad
          if (firstDayIndex !== -1 && i < firstDayIndex) {
            let prevMonth = importMonth - 1;
            let prevYear = importYear;
            if (prevMonth < 1) { prevMonth = 12; prevYear--; }

            return { ...pos, month: prevMonth, year: prevYear };
          }

          // Efter första 1 → vald månad tills vi träffar nästa 1
          if (i === firstDayIndex) {
            currentMonth = importMonth;
            currentYear = importYear;
          }

          // Om vi stöter på en ny 1 efter att ha sett höga dagar → nästa månad
          if (i > firstDayIndex && day === 1 && datePositions[i - 1].date > 20) {
            currentMonth++;
            if (currentMonth > 12) { currentMonth = 1; currentYear++; }
          }
          

          return { ...pos, month: currentMonth, year: currentYear };
        });



        console.groupCollapsed("%c[Infer] Datum m/år (före filter)", "color:#b58900;font-weight:bold");
        console.table(datePositions.slice(0, 100));
        console.groupEnd();

        // Filtrera på vald månad
        const targetMonth = parseInt(selectedMonth, 10);
        datePositions = datePositions.filter(p => p.month === targetMonth);

        console.groupCollapsed("%c[Filter] Datum i vald månad", "color:#b58900;font-weight:bold");
        console.table(datePositions.slice(0, 100));
        console.groupEnd();

        if (!datePositions.length) {
          console.warn("Inga datum kvar efter filtrering på månad =", targetMonth);
        }

        // Hjälp: hitta nästa datum i EN LÄGRE RAD (start på nästa block/vecka)
        const findNextLowerRowIndex = (i) => {
          const curRow = datePositions[i].row;
          for (let j = i + 1; j < datePositions.length; j++) {
            if (datePositions[j].row > curRow) return j;
          }
          return -1; // inget längre ned -> använd nederkant av arket
        };

        // ===== 3) Samla rader för varje datum (fast kolumnfönster) =====
        const MAX_EMPTY_STREAK = 50;

        for (let i = 0; i < datePositions.length; i++) {
          const cur = datePositions[i];
          const startRow = cur.row + 1;
          const nextLowerIdx = findNextLowerRowIndex(i);
          const hardEnd  = nextLowerIdx !== -1 ? datePositions[nextLowerIdx].row : rows.length;

          // Bounds-koll för kolumner
          const validCols = cur.processColumns.map(ci => (ci < 0 ? null : ci));
          if (validCols[0] === null || validCols[1] === null || validCols[2] === null) {
            console.warn(`[Dag ${cur.date}] Hoppar över pga out-of-bounds processColumns:`, cur.processColumns);
            cur.processRows = [];
            continue;
          }

          console.groupCollapsed(`%c[Dag ${cur.date}] Läsning`, "color:#d33682;font-weight:bold");
          console.log("Header (row,col):", cur.row, cur.col, "Fönster (proj,tid,anst):", cur.processColumns, "startRow:", startRow, "hardEnd:", hardEnd);

          let processRows = [];
          let lastNonEmptyIdx = -1;
          let emptyStreak = 0;

          for (let r = startRow; r < hardEnd; r++) {
            const raw = rows[r] || [];
            const row3 = validCols.map(ci => raw[ci] ?? null);
            const nonEmpty = row3.some(v => v != null && String(v).trim() !== "");
            processRows.push(row3);
            if (nonEmpty) { lastNonEmptyIdx = processRows.length - 1; emptyStreak = 0; }
            else { emptyStreak++; if (emptyStreak >= MAX_EMPTY_STREAK) break; }
          }

          if (lastNonEmptyIdx >= 0) processRows = processRows.slice(0, lastNonEmptyIdx + 1);
          else processRows = [];

          cur.processRows = processRows;

          console.log("Antal rader insamlade:", processRows.length);
          console.table(processRows.slice(0, 12));
          console.groupEnd();
        }

        // ===== 4) Bygg shifts (projekt = col0, tid = col1, anställd = col2) =====
        const finalShiftData = datePositions.map((datePos) => {
          const day = datePos.date;
          const fullDate =
            `${datePos.year}-${String(datePos.month).padStart(2,"0")}-${String(day).padStart(2,"0")}`;

            const BLOCKED_PROJECTS = new Set([
              "power barkarby",
              "power länna",
              "power nacka",
            ]);

            const shifts = (datePos.processRows || [])
              .filter(row => {
                if (!row.some(v => v !== null && String(v).trim() !== "")) {
                  return false;
                }

                const projectName = String(row[0] || "")
                  .trim()
                  .toLowerCase();

                return !BLOCKED_PROJECTS.has(projectName);
              })
              .map(row => {
              const projectCell   = row[0] ?? null;
              const timeCell      = row[1] ?? null;
              const employeeCell  = row[2] ?? null;

              let startTime = null, endTime = null;
              if (typeof timeCell === 'number') {
                startTime = fromExcelTimeNumber(timeCell);
              } else if (typeof timeCell === 'string') {
                const { start, end } = parseAnyTimeRange(timeCell);
                startTime = start; endTime = end;
              }

              return {
                project: (projectCell !== null && projectCell !== "") ? String(projectCell).trim() : null,
                startTime,
                endTime,
                employee: (employeeCell !== null && employeeCell !== "") ? String(employeeCell).trim() : null
              };
            });

          console.groupCollapsed(`%c[Dag ${fullDate}] Shifts byggda`, "color:#859900;font-weight:bold");
          console.log("Antal shifts:", shifts.length);
          console.table(shifts.slice(0, 15));
          console.groupEnd();

          return { date: fullDate, shifts };
        });

        console.groupCollapsed("%c[Final] Sammanfattning", "color:#6c71c4;font-weight:bold");
        console.log("Antal dagar:", finalShiftData.length);
        finalShiftData.forEach(d => console.log(d.date, "shifts:", d.shifts.length));
        console.groupEnd();

        setParsedShifts(finalShiftData);
        setShowPreviewPopup(true);
        setPopupLoading(false);
      };
    } catch (error) {
      console.error("❌ Error processing file:", error);
      setPopupLoading(false);
      alert("❌ Ett fel uppstod vid uppladdning.");
    } finally {
      setPopupLoading(false);
    }
  };

  // ===== preview-summering =====
  let invalidCount = 0;
  let totalCount = 0;
  parsedShifts.forEach(day => {
    day.shifts.forEach(shift => {
      totalCount++;
      const errors = [];
      if (!shift.project) errors.push('Projekt saknas');
      if (!shift.startTime || !shift.endTime) {
        errors.push('Tid saknas');
      } else {
        if (!timeFormatRegex.test(shift.startTime) || !timeFormatRegex.test(shift.endTime)) {
          errors.push('Felaktigt tidformat (måste vara HH:MM)');
        }
      }
      if (!shift.employee) errors.push('Anställd saknas');
      if (errors.length > 0) invalidCount++;
    });
  });

  return (
    <div style={styles.overlay} onClick={popupLoading ? null : onClose}>
      <div style={{ ...styles.wrapper, position: 'relative' }} onClick={(e) => e.stopPropagation()}>
        <style>{`
          .popup-loading-spinner { border: 4px solid rgba(0,0,0,0.1); border-top: 4px solid #5DADE2; border-radius: 50%; width: 42px; height: 42px; animation: spin 0.7s linear infinite; }
          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        `}</style>

        {popupLoading && (
          <div style={styles.loadingOverlay}>
            <div className="popup-loading-spinner" />
            <p style={{ marginTop: '1rem', fontSize: '1rem', color: '#444', fontWeight: 500 }}>
              Importerar skift från filen… <br />Vänligen Vänta
            </p>
          </div>
        )}

        {popupLoading && (
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1, pointerEvents: 'auto', backgroundColor: 'transparent' }} />
        )}

        <div style={{ padding: "2rem", opacity: popupLoading ? 0.4 : 1, pointerEvents: popupLoading ? 'none' : 'auto' }}>
          <h2>Importera schema från Excel</h2>
          <p>Välj en Excel-fil med information om arbetspassen för att lägga till dem i schemat.</p>
          <p style={{fontSize: 12, marginTop: 5}}>Se till att den har rätt format som i mallen för att allt ska importeras korrekt.</p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '12px 0' }}>
            <button type="button" onClick={() => document.getElementById('file-input').click()}
              style={{ padding: '12px 20px', fontSize: '14px', fontFamily: 'sans-serif', backgroundColor: "#1da65a", color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', margin: '8px 0' }}>
              Ladda upp fil..
            </button>
            <span id="file-name" style={{ fontSize: '14px', fontFamily: 'sans-serif', color: '#333' }}>Ingen fil vald</span>
            <input id="file-input" type="file" accept=".xlsx, .xls, .ods"
              onChange={e => { handleFileChange(e); const name = e.target.files[0]?.name || 'Ingen fil vald'; document.getElementById('file-name').textContent = name; }}
              style={{ display: 'none' }} />
          </div>
          <br></br>



          <div style={styles.dateRow}>
            <label>Månad:</label>
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} style={styles.input}>
              <option value="">månad</option>
              {[...Array(12).keys()].map((i) => (
                <option key={i + 1} value={i + 1}>{new Date(0, i).toLocaleString("sv-SE", { month: "long" })}</option>
              ))}
            </select>

            <label>År:</label>
            <input type="number" value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value) || new Date().getFullYear())}
              placeholder="Välj år" style={styles.input} min="2000" max="2100" step="1" onKeyDown={(e) => e.preventDefault()} />
          </div>

          {errorMessages.length > 0 && (
            <div style={styles.errorBox}>
              {errorMessages.map((error, index) => (<p key={index} style={styles.errorText}>{error}</p>))}
            </div>
          )}

          <div style={styles.buttonRow}>
            <button onClick={() => window.open("https://drive.google.com/file/d/13-WbYGNOdBsrWGwHCmtj5UYp24-ChdRy/view?usp=sharing", "_blank")} style={styles.downloadButton}>📥 Ladda ner mall</button>
            <button onClick={onClose} style={styles.cancelButton}>Avbryt</button>
            <button style={styles.importButton} onClick={handleFileUpload} disabled={!selectedFile || !selectedMonth || !selectedYear}>Importera</button>
          </div>
        </div>
      </div>

      {showPreviewPopup && (
        <div onClick={(e) => e.stopPropagation()} style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '10px', width: '90%', maxWidth: '1000px', maxHeight: '90vh', overflowY: 'auto', padding: '1rem', position: 'relative' }}>
            {confirmLoading && (
              <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(255, 255, 255, 0.5)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', zIndex: 10000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: '36px', height: '36px', border: '4px solid rgba(0, 0, 0, 0.1)', borderTop: '4px solid #5DADE2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                <p style={{ marginTop: '1rem', fontSize: '0.95rem', color: '#444', opacity: 0.85, fontWeight: 500 }}>
                  Importerar skift... <br />Vänligen vänta
                </p>
              </div>
            )}

            <div style={{ position: 'sticky', top: 0, backgroundColor: '#f9f9f9', padding: '0.8rem 1rem', borderBottom: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 10 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <div style={{ fontSize: '1rem', fontWeight: 'bold' }}>{totalCount} pass hittades • {invalidCount} pass innehåller fel</div>
                <div style={{ fontSize: '0.85rem', color: '#555' }}>För att resultaten ska bli korrekta är det viktigt att namn och projekt i filen matchar de som är tillagda i TimeSmart.</div>
                <div style={{ fontSize: '0.85rem', color: '#b30000', fontWeight: 'bold' }}>Rödmarkerade pass importeras men markeras som ofullständiga.</div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => setShowPreviewPopup(false)} style={{ background: '#bbb', padding: '0.5rem 1.2rem', borderRadius: '6px', fontWeight: 'bold', color: '#fff' }}>Stäng</button>
                {/* NEW: disabled while confirmLoading to block double click */}
                <button
                  onClick={handleConfirmImport}
                  disabled={confirmLoading}
                  aria-busy={confirmLoading}
                  style={{ background: '#5DADE2', padding: '0.5rem 1.2rem', borderRadius: '6px', color: '#fff', fontWeight: 'bold', opacity: confirmLoading ? 0.7 : 1, cursor: confirmLoading ? 'not-allowed' : 'pointer' }}
                >
                  Importera
                </button>
              </div>
            </div>

            {parsedShifts.map(day => (
              <div key={day.date} style={{ marginBottom: '1rem' }}>
                <h4>{day.date}</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={tableCellStyle}>Projekt</th>
                      <th style={tableCellStyle}>Starttid</th>
                      <th style={tableCellStyle}>Sluttid</th>
                      <th style={tableCellStyle}>Anställd</th>
                      <th style={tableCellStyle}>Fel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {day.shifts.map((shift, idx) => {
                      const errors = [];
                      if (!shift.project) errors.push('Projekt saknas');
                      if (!shift.startTime || !shift.endTime) {
                        errors.push('Tid saknas');
                      } else {
                        if (!timeFormatRegex.test(shift.startTime) || !timeFormatRegex.test(shift.endTime)) {
                          errors.push('Felaktigt tidformat (måste vara HH:MM)');
                        }
                      }
                      if (!shift.employee) errors.push('Anställd saknas');

                      return (
                        <tr key={idx} style={{ backgroundColor: errors.length ? '#ffe6e6' : 'transparent' }}>
                          <td style={tableCellStyle}><input type="text" value={shift.project || ''} onChange={(e) => updateShift(day.date, idx, 'project', e.target.value)} style={{ width: '100%' }} /></td>
                          <td style={tableCellStyle}><input type="text" value={shift.startTime || ''} onChange={(e) => updateShift(day.date, idx, 'startTime', e.target.value)} style={{ width: '100%' }} /></td>
                          <td style={tableCellStyle}><input type="text" value={shift.endTime || ''} onChange={(e) => updateShift(day.date, idx, 'endTime', e.target.value)} style={{ width: '100%' }} /></td>
                          <td style={tableCellStyle}><input type="text" value={shift.employee || ''} onChange={(e) => updateShift(day.date, idx, 'employee', e.target.value)} style={{ width: '100%' }} /></td>
                          <td style={tableCellStyle}>{errors.join(', ') || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const tableCellStyle = {
  border: '1px solid #ddd',
  padding: '8px',
  textAlign: 'left',
  fontSize: '0.9rem',
  whiteSpace: 'nowrap'
};

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 9999
  },
  wrapper: {
    backgroundColor: 'white', borderRadius: '10px', width: '600px',
    maxWidth: '95%', position: 'relative', boxShadow: '0 0 15px rgba(0,0,0,0.2)'
  },
  inputFile: { margin: '1rem 0' },
  dateRow: { display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' },
  input: { padding: '0.5rem', border: '1px solid #ccc', borderRadius: '5px', fontSize: '1rem' },
  errorBox: { marginTop: '1rem', backgroundColor: '#ffe6e6', padding: '0.8rem', borderRadius: '6px', border: '1px solid #ff4d4d' },
  errorText: { color: '#b30000', margin: 0 },
  buttonRow: { marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem', flexWrap: 'wrap' },
  cancelButton: { padding: '0.6rem 1.2rem', backgroundColor: '#bbb', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer', fontWeight: 'bold' },
  importButton: { cursor: 'pointer',padding: '0.6rem 1.2rem', backgroundColor: '#217346', border: 'none', borderRadius: '6px', color: 'white', fontWeight: 'bold' },
  downloadButton: { padding: '0.6rem 1.2rem', backgroundColor: "#1da65a", border: 'none', borderRadius: '6px', color: 'white', fontWeight: 'bold', cursor: 'pointer' },
  loadingOverlay: {
    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.75)', zIndex: 2, display: 'flex',
    flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    borderRadius: '10px', padding: '2rem', textAlign: 'center'
  }
};
